import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  pending: { label: 'En attente', cls: 'text-amber-700 border-amber-300 bg-amber-50' },
  approved: { label: 'Approuvée', cls: 'text-emerald-700 border-emerald-300 bg-emerald-50' },
  rejected: { label: 'Refusée', cls: 'text-red-700 border-red-300 bg-red-50' },
};

export default function AdminRegistrations() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState(null);
  const [grids, setGrids] = useState([]);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [gridSource, setGridSource] = useState('none');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/registrations`, { params: { status: tab } });
      setItems(data);
    } catch (e) { toast.error('Erreur de chargement'); }
  }, [tab]);

  useEffect(() => { setItems(null); fetchItems(); }, [fetchItems]);
  useEffect(() => {
    axios.get(`${API}/price-grids`).then((r) => setGrids(r.data)).catch(() => {});
  }, []);

  const approve = async () => {
    setBusy(true);
    try {
      const body = gridSource !== 'none' ? { grid_source_cabinet_id: gridSource } : {};
      await axios.post(`${API}/admin/registrations/${approveTarget.id}/approve`, body);
      toast.success(`Compte créé pour ${approveTarget.cabinet_name}`);
      setApproveTarget(null); setGridSource('none');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'approbation");
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/registrations/${rejectTarget.id}/reject`, { reason });
      toast.success('Demande refusée');
      setRejectTarget(null); setReason('');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Inscriptions" subtitle="Demandes d'inscription des cabinets dentaires">
      <div className="space-y-4" data-testid="admin-registrations">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="pending" data-testid="registrations-tab-pending">En attente</TabsTrigger>
            <TabsTrigger value="approved" data-testid="registrations-tab-approved">Approuvées</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="registrations-tab-rejected">Refusées</TabsTrigger>
          </TabsList>
        </Tabs>

        {items === null ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Aucune demande.</p>
        ) : (
          <div className="space-y-3">
            {items.map((r) => (
              <Card key={r.id} className="border-border" data-testid={`registration-row-${r.id}`}>
                <CardContent className="py-4 flex items-center justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold">{r.cabinet_name}</p>
                      <Badge variant="outline">{r.structure}</Badge>
                      <Badge variant="outline" className={STATUS_BADGE[r.status]?.cls}>{STATUS_BADGE[r.status]?.label}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {r.first_name} {r.last_name} · {r.contact_email}
                      {r.telephone && ` · ${r.telephone}`}{r.city && ` · ${r.city}`}
                    </p>
                    {r.message && <p className="text-xs text-muted-foreground italic mt-1">« {r.message} »</p>}
                    {r.rejection_reason && <p className="text-xs text-red-600 mt-1">Motif du refus : {r.rejection_reason}</p>}
                  </div>
                  {r.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => setApproveTarget(r)} data-testid={`registration-approve-${r.id}`}>
                        <CheckCircle2 className="h-4 w-4" />Approuver
                      </Button>
                      <Button size="sm" variant="outline" className="gap-1 text-red-600"
                        onClick={() => setRejectTarget(r)} data-testid={`registration-reject-${r.id}`}>
                        <XCircle className="h-4 w-4" />Refuser
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!approveTarget} onOpenChange={(o) => !o && setApproveTarget(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approuver l'inscription</DialogTitle>
            <DialogDescription>
              Le compte cabinet <b>{approveTarget?.cabinet_name}</b> sera créé et un email de bienvenue envoyé.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Grille tarifaire (copie d'une grille existante)</Label>
            <Select value={gridSource} onValueChange={setGridSource}>
              <SelectTrigger data-testid="approve-grid-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucune grille pour le moment</SelectItem>
                {grids.map((g) => (
                  <SelectItem key={g.id} value={g.cabinet_id}>{g.cabinet_name} (v{g.version} · {g.lines?.length || 0} lignes)</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">La grille active du cabinet sélectionné sera copiée en version 1 pour le nouveau cabinet.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>Annuler</Button>
            <Button onClick={approve} disabled={busy} data-testid="approve-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Créer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Refuser la demande</DialogTitle>
            <DialogDescription>Un email sera envoyé à <b>{rejectTarget?.contact_email}</b>.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Motif (facultatif)</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Annuler</Button>
            <Button variant="destructive" onClick={reject} disabled={busy} data-testid="reject-confirm-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Refuser
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
